/**
 * IngresoRepository - Repositorio unificado para Ingresos
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 * Contiene toda la lógica de negocio de ingresos, delegando las operaciones
 * de persistencia al IDatabaseAdapter específico de cada plataforma.
 */

import { v4 as uuidv4 } from 'uuid';
import { IIngresoRepository } from '../interfaces/IIngresoRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { Ingreso, FiltroIngresos } from '../interfaces/entities';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Ingresos
 *
 * Este repositorio funciona tanto en web (con DexieAdapter) como en mobile (con SQLiteAdapter).
 */
export class IngresoRepository extends BaseRepository<Ingreso, string> implements IIngresoRepository {
  private profileRepo: IProfileRepository | null = null;

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'ingresos', 'ingresos'); // tabla local, tabla Supabase

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    // Si se proporciona el profileRepository, inyectarlo
    if (profileRepo) {
      this.profileRepo = profileRepo;
      this.setProfileRepository(profileRepo);
    }
  }

  // ==================== CRUD Básico ====================

  async add(data: Omit<Ingreso, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: Ingreso = {
      ...data,
      id,
      updated_at: now,
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      user_id: data.user_id || profile?.user_id || '',
      is_deleted: false,
      fecha_evento: data.fecha_evento || now
    };

    await this.db.insert('ingresos', record);
    this.emitSync('INSERT', record);

    return id;
  }

  async update(data: Partial<Ingreso> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('ingresos', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.db.delete('ingresos', id);
    this.emitSync('DELETE', id);
  }

  // ==================== Soft Delete ====================
  // Estos métodos ahora vienen de BaseRepository (soporta soft delete habilitado)

  // ==================== Queries por Filtros ====================

  async getByFiltros(filtros: FiltroIngresos): Promise<Ingreso[]> {
    // Partimos de los registros de la empresa activa para no mezclar datos
    // de otras empresas.
    const base = (await this.getAllForActiveEmpresa()).filter(i => !i.is_deleted);

    const filtrados = base.filter(i => {
      if (filtros.fecha_inicio && i.fecha_evento < filtros.fecha_inicio) return false;
      if (filtros.fecha_fin && i.fecha_evento > filtros.fecha_fin) return false;
      if (filtros.concepto && !(i.concepto ?? '').toLowerCase().includes(filtros.concepto.toLowerCase())) return false;
      if (filtros.categoria && i.categoria !== filtros.categoria) return false;
      return true;
    });

    return filtrados.sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async getByPeriodo(fechaInicio: string, fechaFin: string): Promise<Ingreso[]> {
    // Filtramos por empresa activa primero para no mezclar datos de otras empresas.
    const deEmpresa = await this.getAllForActiveEmpresa();
    return deEmpresa
      .filter(i => !i.is_deleted && i.fecha_evento >= fechaInicio && i.fecha_evento <= fechaFin)
      .sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async getByCategoria(categoria: string): Promise<Ingreso[]> {
    const all = await this.getAllForActiveEmpresa();
    return all
      .filter(i => !i.is_deleted && i.categoria === categoria)
      .sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async searchByConcepto(concepto: string): Promise<Ingreso[]> {
    const lower = concepto.toLowerCase();
    const all = await this.getAllForActiveEmpresa();
    return all
      .filter(i => !i.is_deleted && (i.concepto ?? '').toLowerCase().includes(lower))
      .sort((a, b) => (b.fecha_evento ?? '').localeCompare(a.fecha_evento ?? ''));
  }

  async getTotalByPeriodo(fechaInicio: string, fechaFin: string): Promise<number> {
    const ingresos = await this.getByPeriodo(fechaInicio, fechaFin);
    return ingresos.reduce((total, i) => total + (i.monto || 0), 0);
  }

  async getGroupedByCategoria(fechaInicio?: string, fechaFin?: string): Promise<Map<string, number>> {
    const ingresos = fechaInicio && fechaFin
      ? await this.getByPeriodo(fechaInicio, fechaFin)
      : await this.getAllForActiveEmpresa();

    const grouped = new Map<string, number>();
    ingresos.forEach(i => {
      const cat = i.categoria || 'Sin categoría';
      grouped.set(cat, (grouped.get(cat) || 0) + (i.monto || 0));
    });
    return grouped;
  }

  async getByEmpresa(empresaId: string): Promise<Ingreso[]> {
    return this.db.queryMultiple<Ingreso>('ingresos', [
      { field: 'is_deleted', operator: 'equals', value: false },
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ], { column: 'fecha_evento', direction: 'desc' });
  }

  // ==================== Bulk Operations ====================
  // Estos métodos ahora vienen de BaseRepository

  // ==================== Fin ====================

  /**
   * Establece el repositorio de perfiles (inyección de dependencia)
   */
  setProfileRepository(profileRepo: IProfileRepository): void {
    this.profileRepo = profileRepo;
    super.setProfileRepository(profileRepo);
  }
}
